import { identity, isPresent } from '@yp/common/identity.ts';
import { asNumber } from '@yp/common/math.ts';
import type {
  DescriptorType,
  RefId,
  Struct,
  TypeDescriptor,
  ValueType,
} from '@yp/common/schema/descriptor.ts';
import { DateTime, fromRfc3339, toIso8601 } from '@yp/common/time.ts';
import { Scalar, type Quantity } from '@yp/common/units.ts';

export const CommonStyle = {
  input:
    'block w-full sm:text-sm sm:leading-6 text-gray-900 disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-500 disabled:ring-gray-200 placeholder:text-gray-400 ',
  transition: 'transition-colors ease-in-out duration-150',
};

export const ToggleStyle = {
  div: 'flex items-center',
  label: 'ml-3 text-sm font-medium text-nowrap',
  input:
    'bg-gray-200 relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus-within:outline-hidden focus-within:ring-2 focus-within:ring-indigo-600 focus-within:ring-offset-2',
  buttonSpan:
    'pointer-events-none flex flex-row items-center justify-center h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out',
};
export const InputStyles = {
  inset: {
    div: 'rounded-md px-3 pb-1.5 pt-2.5 shadow-xs ring-1 ring-inset ring-gray-300 focus-within:ring-2 focus-within:ring-indigo-600',
    label: `${CommonStyle.transition} block text-xs font-medium`, // text-gray-700',
    border: '',
    input: `${CommonStyle.input} ${CommonStyle.transition} border-0 p-0 focus-within:ring-0`,
    textarea: `${CommonStyle.input} ${CommonStyle.transition} border-0 p-0 focus-within:ring-0 resize-none isolate`,
    datepart: 'border-0',
  },
  overlap: {
    div: 'relative mt-4',
    label: 'absolute -top-2 left-2 inline-block bg-white px-1 text-xs font-medium', // text-gray-900',
    border:
      'rounded-md border-0 py-1.5 shadow-xs ring-1 ring-inset ring-gray-300 focus-within:ring-2 focus-within:ring-inset focus-within:ring-indigo-600',
    input: `${CommonStyle.input} px-3 py-1.5`,
    textarea: `min-h-16 ${CommonStyle.input} rounded-md border-0 py-1.5 shadow-xs ring-1 ring-inset ring-gray-300 focus-within:ring-2 focus-within:ring-inset focus-within:ring-indigo-600 resize-none`, // 'isolate'
    datepart: 'leading-6 p-0 border-0 !ring-0',
  },
} as const satisfies Record<
  string,
  { div: string; label: string; border: string; input: string; textarea: string; datepart: string }
>;

export type InputMode =
  | 'none'
  | 'text'
  | 'decimal'
  | 'numeric'
  | 'tel'
  | 'search'
  | 'email'
  | 'url';

export type InputAttribute<T extends ValueType> = {
  type: (desc: TypeDescriptor<T>) => {
    type: string;
    inputmode?: InputMode | undefined;
    pattern?: string | undefined;
  };
  toInput: (value: T | undefined, desc: TypeDescriptor<T>) => string;
  fromInput: (value: string, desc: TypeDescriptor<T>) => T | undefined;
};

const stringAttribute: InputAttribute<string> = {
  type: (desc) => {
    switch (desc.st) {
      case 'multi':
        return { type: 'textarea' };
      case 'email':
        return { type: 'email' };
      case 'phone':
        return { type: 'tel' };
      case 'numeric':
        return { type: 'text', inputmode: 'numeric', pattern: '\\d*' };
      default:
    }
    return { type: 'text' };
  },
  toInput: (v) => v ?? '',
  fromInput: identity,
};
const numberAttribute: InputAttribute<number> = {
  type: () => ({ type: 'number' }),
  toInput: (v) => String(v ?? ''),
  fromInput: asNumber,
};
const scalarAttribute: InputAttribute<Scalar<Quantity>> = {
  type: () => ({ type: 'number' }),
  toInput: (v, desc) => String(v?.value(desc.u) ?? ''),
  fromInput: (v, desc) => {
    const value = asNumber(v);
    return isPresent(value) ? Scalar.from(desc.u, value) : undefined;
  },
};
const datetimeAttribute: InputAttribute<DateTime<true>> = {
  type: (v) => ({ type: v.st ?? 'timestamp' }),
  toInput: (v) => toIso8601(v) ?? '0000-00-00',
  fromInput: (v) => {
    const result = fromRfc3339(v);
    if (result && result.year < 1000) {
      return undefined;
    }
    return result;
  },
};
const boolAttribute: InputAttribute<boolean> = {
  type: () => ({ type: 'bool' }),
  toInput: (v) => String(v ?? ''),
  fromInput: (v) => v === 'true',
};
// const arrayAttribute: InputAttribute<Array<RefId<any> | string>> = {
//   type: () => ({ type: 'array' }),
//   toInput: (v) => (v ? v.join(',') : ''),
//   fromInput: (v) => v.split(','),
// };

export const InputAttributes = {
  string: stringAttribute,
  number: numberAttribute,
  scalar: scalarAttribute,
  datetime: datetimeAttribute,
  bool: boolAttribute,
  // array: arrayAttribute,
  // ref: refAttribute,
} as const satisfies Partial<Record<DescriptorType, unknown>>;

type TypeOfAttribute<X> = X extends InputAttribute<infer Y> ? Y : never;

export type SupportedTypes =
  | TypeOfAttribute<(typeof InputAttributes)[keyof typeof InputAttributes]>
  | RefId<Struct>
  | Array<string | number | RefId<Struct>>;
// | boolean;

export type FormType = { [key: string]: SupportedTypes };
// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export type EmptyForm = {};

function updateTextAreaHeight(
  el: HTMLTextAreaElement | undefined,
  params: { lineHeight: number | undefined },
) {
  const maxTextAreaLines = 6;
  const borderWidth = 1;
  const lineHeightFudge = 2;

  if (el) {
    if (params.lineHeight === undefined) {
      const style = getComputedStyle(el);
      params.lineHeight = parseFloat(style.lineHeight) + lineHeightFudge;
    }
    el.style.height = '0';
    const newHeight = `${Math.min(
      maxTextAreaLines * params.lineHeight,
      el.scrollHeight + 2 * borderWidth,
    )}px`;
    el.style.height = newHeight;
  }
}

export function watchTextAreaHeight(node: HTMLTextAreaElement, _value: unknown) {
  // TODO: Reset this if the font size changes
  const params = { lineHeight: undefined };
  updateTextAreaHeight(node, params);
  return {
    update(_value: unknown) {
      updateTextAreaHeight(node, params);
    },
  };
}
